import { BorderColorOutlined, DeleteOutline, InfoOutlined } from "@mui/icons-material";
import {
    Box,
    Button,
    Card,
    CardContent,
    Container,
    Divider,
    IconButton,
    Tooltip,
    Typography
} from "@mui/material";
import Prism from 'prismjs';
import 'prismjs/components/prism-clike';
import 'prismjs/components/prism-rego';
import 'prismjs/themes/prism.css';
import InfoIcon from '@mui/icons-material/Info';
import ModalWrapper from "../common/modal/wrapper";
import AddPolicyForm from "./addPolicies";
import DeletePolicyForm from "./deletePolicyForm";
import ReactMarkdownStyled from "../common/reactMarkdownStyled";
import { useHistory, useParams, withRouter } from "react-router-dom";
import { useEffect, useState } from "react";
import { IAlertState, useAlertState } from "../../store/alertState";
import { axiosInstance } from "../../services/axios";
import { API_SLUGS } from "../../constants";
import CommonPopper from "../common/commonPopper";
import Editor from 'react-simple-code-editor';

const DEFAULT_POLICY_CODE_BASE64 = "cGFja2FnZSBhcHRvcmkKCnZpb2xhdGlvbnMgY29udGFpbnMgewoJInJ1bGVOYW1lIjogIlBhc3N3b3JkIFN0cmVuZ3RoIiwKCSJzZXZlcml0eSI6ICJoaWdoIiwKCSJyZWFzb25zIjogUGFzc3dvcmRWaW9sYXRpb25zLAp9IGlmIHsKCWNvdW50KFBhc3N3b3JkVmlvbGF0aW9ucykgPiAwCn0KClBhc3N3b3JkVmlvbGF0aW9ucyBjb250YWlucyAibXVzdCBoYXZlIGxvd2VyLWNhc2UgY2hhcmFjdGVyIiBpZiB7Cglub3QgcmVnZXgubWF0Y2goYFthLXpdYCwgdXBkYXRlZFBhc3N3b3JkKQp9CgpQYXNzd29yZFZpb2xhdGlvbnMgY29udGFpbnMgIm11c3QgaGF2ZSB1cHBlci1jYXNlIGNoYXJhY3RlciIgaWYgewoJbm90IHJlZ2V4Lm1hdGNoKGBbQS1aXWAsIHVwZGF0ZWRQYXNzd29yZCkKfQoKIyB1cGRhdGVkUGFzc3dvcmQgY29udGFpbnMgdGhlIHZhbHVlIG9mIGEgcGFzc3dvcmQgdGhhdCB3YXMKIyBhY2NlcHRlZCBieSBvbmUgb2YgcGFzc3dvcmRNb2RpZnlPcHMuCnVwZGF0ZWRQYXNzd29yZCA6PSB4IGlmIHsKCXNvbWUgc3RlcCBpbiBpbnB1dC5zZXF1ZW5jZQoJc3RlcC5zdGF0dXMgPT0gInN1Y2Nlc3MiCglzdGVwLm1ldGhvZCBpbiB7IlBPU1QiLCAiUFVUIn0KCXggOj0gc3RlcC5pbnB1dC5ib2R5LnBhc3N3b3JkCn0="
const POLICY_CODE_INFO = "Define a [Rego policy](https://www.openpolicyagent.org/docs/latest/policy-language/) to be evaluated by the [`PolicyEvaluator` analyzer](https://docs.aptori.dev/sift/analyzers/policy-evaluator/). A Rego policy used with the PolicyEvaluator must declare the package as 'aptori' and define a 'violations' rule.  The 'violations' rule must return an array of violation objects when a request sequence violates a policy rule.  Each violation object must include a rule name, severity, and an array of reasons explaining why the rule was violated."

interface IPolicyData {
    selectedPolicy: string
}

const PolicyData: React.FC<any> = () => {

    const { policyId }: any = useParams();
    const [loading, setLoading] = useState(false);
    const { setMessage } = useAlertState() as IAlertState;
    const [tableData, setTableData] = useState<any[]>([]);
    const [isDataChanged, setIsDataChanged] = useState(false);
    const [initialData, setInitialData] = useState(null);
    const [policyCode, setPolicyCode] = useState<string>("");
    const [validationError, setValidationError] = useState<any>({});
    const [policyName, setPolicyName] = useState<string>(" ");
    const [policyResponse, setPolicyResponse] = useState<any>();
    const navigate = useHistory();

    useEffect(() => {
        fetchPolicyData(true)
    }, [])

    const fetchPolicyData = async (initialize = false) => {

        try {
            setLoading(true);
            let policyData = await axiosInstance.get(API_SLUGS.TOOL_POLICY.replace('{id}', policyId));

            setPolicyResponse(policyData?.data);
            setPolicyCode(atob(policyData?.data?.data || DEFAULT_POLICY_CODE_BASE64))
            setPolicyName(policyData?.data?.name)
            const fetchedData = policyData?.data?.rules || [];

            if (initialize) {
                setInitialData(JSON.parse(JSON.stringify(fetchedData)));
            }
            setLoading(false);
        } catch (error: any) {
            setMessage({ title: error.response?.data?.message, type: "error" });
            setLoading(false)
        }

    }


    const handleSave = async () => {

        try {
            const updatedObject = {
                rules: trimTableData(),
                data: btoa(policyCode)
            }

            const response = await axiosInstance.patch(API_SLUGS.TOOL_POLICY.replace('{id}', policyId), updatedObject);
            setMessage({ title: "Policy updated successfully", type: "success" });

            fetchPolicyData(true);
        } catch (error: any) {
            setMessage({ title: error.response?.data?.message, type: "error" });
        }
    }
    const trimTableData = () => {
        const trimmedTableData = [...tableData];
        let newIndex = 0;
        tableData.forEach((data: any, index: number) => {

            if (data.name === "" && data.severity === "") {
                trimmedTableData.splice(newIndex, 1);
                newIndex--
            }
            newIndex++
        })
        return trimmedTableData;
    }
    const handleCodeChange = (code: string) => {
        setPolicyCode(code)
    }

    const validateData = () => {

        const tableDataToSend = trimTableData();
        let isValidated = true;
        setValidationError("");
        tableDataToSend.forEach((rule: any, index: number) => {
            const { name, severity } = rule;
            if (!name || name === "") {
                isValidated = false;
                setValidationError({ message: "Name cannot be empty.", index, cellName: "name" });
            }
            if (!severity || severity === "") {
                isValidated = false;
                setValidationError({ message: `Severity cannot be empty.`, index, cellName: "severity" });
            }
        });
        return { data: tableDataToSend, isValidated };
    }


    useEffect(() => {
        if (initialData) {
            setIsDataChanged(JSON.stringify(initialData) !== JSON.stringify(trimTableData()));
        }
    }, [initialData, trimTableData()]);


    return (
        <>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    minHeight: '90%',
                    m: 2
                }}
            >
                <Container maxWidth="xl" sx={{ px: 3, pt: 2, pb: 0 }}>



                    <Box
                        sx={{
                            textAlign: "left",
                        }}
                    >

                        <Typography color="textSecondary" gutterBottom variant="overline">
                            Policy
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "start" }}>
                            <Typography color="textPrimary" variant="h4" sx={{ lineHeight: "0.75em" }}>
                                {policyName}
                            </Typography>

                            <ModalWrapper
                                maxWidth="sm"
                                title="Edit Policy"
                                trigger={

                                    <Tooltip title="Edit">
                                        <Button variant='outlined' size='small' sx={{ color: 'text.primary', borderColor: 'neutral.300', mr: 1, minWidth: '45px', mx: 1 }}>
                                            <BorderColorOutlined />
                                        </Button>
                                    </Tooltip>

                                }
                                child={
                                    <AddPolicyForm
                                        callback={() => fetchPolicyData()}
                                        preventRedirect
                                        defaultFormValues={policyResponse}
                                        updateOnly={true}
                                        updateUUID={policyId}

                                    />
                                }
                            />

                            <ModalWrapper
                                maxWidth="sm"
                                title="Delete Policy"
                                trigger={
                                    <Tooltip title="Delete">
                                        <Button variant='outlined' size='small' color='error' sx={{ borderColor: 'neutral.300', mr: 1, minWidth: '45px', ml: 0.5 }}>
                                            <DeleteOutline />
                                        </Button>
                                    </Tooltip>

                                }
                                child={

                                    <DeletePolicyForm
                                        callback={() => navigate.push("/policies")}
                                        preventRedirect
                                        updateUUID={policyId}
                                        name={policyName}
                                    />
                                }
                            />

                        </Box>
                        <Box sx={{ maxWidth: "50vw" }}>

                            <Typography color="textSecondary" variant="body1" paragraph>{policyResponse?.description}</Typography>

                        </Box>

                    </Box>

                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Box sx={{ display: "flex", justifyContent: "start", mt: 3, alignItems: "center" }}>
                            <Typography
                                sx={{ textAlign: "left" }}
                                variant="body2"
                                color="textSecondary"
                            >
                                Policy Code Editor
                            </Typography>
                            <CommonPopper
                                trigger={
                                    <Tooltip title="Info">
                                        <IconButton size="small">
                                            <InfoOutlined fontSize="inherit" />
                                        </IconButton>
                                    </Tooltip>
                                }
                                child={
                                    <Card elevation={0} sx={{ width: "15em" }}>
                                        <CardContent>
                                            <Typography variant="h6">Information</Typography>
                                            <Divider sx={{ my: 1 }} />
                                            <Box sx={{ fontSize: "0.8em" }}>
                                                <ReactMarkdownStyled
                                                    markdown={POLICY_CODE_INFO}
                                                />
                                            </Box>
                                        </CardContent>
                                    </Card>
                                }
                                placement={"right-end"}

                            />


                        </Box>
                        <Box sx={{ display: "flex", justifyContent: "end", m: 2 }}>
                            <Button variant="contained" onClick={handleSave} size="small" >Save</Button>
                        </Box>
                    </Box>

                    <Box overflow={'auto'} height="70vh">
                        <Editor
                            value={policyCode}
                            onValueChange={code => handleCodeChange(code)}
                            highlight={code => Prism.highlight(code, Prism.languages.rego, 'rego')}
                            padding={10}
                            style={{
                                fontFamily: '"Fira code", "Fira Mono", monospace',
                                fontSize: 12,
                                minHeight: '100%',
                                overflow: 'auto',
                                backgroundColor: "#f5f5f5"
                            }}


                        />
                    </Box>

                </Container>
            </Box>
        </>

    )
};


export default withRouter(PolicyData);